import { creatomateToNormalizedCrop } from '@src/editor/models/layout/CropTransformUtils';
import {
  isApproxEqual,
  POSITION_EPSILON,
} from '@src/utility/approximateEquality';
import { deepClone } from '@src/utility/deepClone';
import { LogManager } from '@src/utility/Logger';

import parsePercent from '../../utility/parsePercent';
import { POSITIONAL_PROPS } from '../models/layout/LayoutEngine';

import type { Element } from '../models/Element';
import type { Scene } from '@src/editor/models/layout/Scene';
import type {
  CropTransformKeyframe,
  ILayoutSegment,
  IScene,
  TrackTransforms,
} from '@src/editor/models/layout/types';
import type { ElementState } from '@src/renderer/ElementState';
import type { RendererState } from '@src/renderer/RendererState';

type SceneLayoutLinkRole = 'parent' | 'composition' | 'representation';

interface SceneLayoutLink {
  parentId?: string;
  representationId: string;
  compositionId: string | null;
  sceneId: string;
  slotName: string;
  compositionTime?: number;
  compositionDuration?: number;
}

interface SceneLayoutIndexRecord {
  link: SceneLayoutLink;
  role: SceneLayoutLinkRole;
}

interface SceneLayoutIndex {
  getRepresentationRoleRecordFor(
    elementId: string,
    referenceTime?: number,
  ): SceneLayoutIndexRecord | undefined;
  getAllRepresentationRecordsFor?(elementId: string): SceneLayoutIndexRecord[];
  findRepresentationRecordByTimeRange?(
    elementId: string,
    absoluteTime: number,
  ): SceneLayoutIndexRecord | undefined;
}

interface RendererSyncDependencies {
  layoutIndexStore: SceneLayoutIndex;
  getScene: (sceneId: string) => Scene | undefined;
  updateScene: (sceneId: string, updates: Partial<IScene>) => boolean;
  updateScenes?: (
    updates: Array<{ sceneId: string; updates: Partial<IScene> }>,
  ) => void;
  updateElement?: (elementId: string, updates: Record<string, any>) => boolean;
  updateElements?: (updates: Map<string, Record<string, any>>) => void;
  normalizeRendererState?: (state: RendererState) => RendererState;
  getCurrentTime?: () => number;
}

interface Candidate {
  element: ElementState;
  keyframe: CropTransformKeyframe;
}

interface SlotSyncState {
  sceneId: string;
  slotName: string;
  layout: ILayoutSegment;
  binding: TrackTransforms;
  currentKeyframe?: CropTransformKeyframe;
  differing: Array<Candidate>;
}

/**
 * Helper class to manage slot state during sync operations.
 * Encapsulates the common pattern of creating/updating slot states
 * and queueing candidate keyframes.
 */
class SlotStateSyncHelper {
  private readonly slotStates: Map<string, SlotSyncState>;
  private readonly sceneLayoutsToUpdate: Map<string, ILayoutSegment>;
  private readonly sceneCache: Map<string, Scene | null>;
  private readonly getSceneFn: (sceneId: string) => Scene | undefined;

  constructor(
    slotStates: Map<string, SlotSyncState>,
    sceneLayoutsToUpdate: Map<string, ILayoutSegment>,
    sceneCache: Map<string, Scene | null>,
    getSceneFn: (sceneId: string) => Scene | undefined,
  ) {
    this.slotStates = slotStates;
    this.sceneLayoutsToUpdate = sceneLayoutsToUpdate;
    this.sceneCache = sceneCache;
    this.getSceneFn = getSceneFn;
  }

  /**
   * Gets or creates a cached scene, logging a warning if not found.
   */
  getOrCacheScene(sceneId: string): Scene | null {
    if (this.sceneCache.has(sceneId)) {
      return this.sceneCache.get(sceneId) ?? null;
    }

    const scene = this.getSceneFn(sceneId);
    if (!scene) {
      logger.warn(
        `RendererSync: scene '${sceneId}' missing while syncing manual layout changes`,
      );
      this.sceneCache.set(sceneId, null);
      return null;
    }

    this.sceneCache.set(sceneId, scene);
    return scene;
  }

  /**
   * Gets or creates a cloned layout for scene updates.
   */
  getOrCreateLayout(sceneId: string, scene: Scene): ILayoutSegment {
    let layout = this.sceneLayoutsToUpdate.get(sceneId);
    if (!layout) {
      layout = deepClone(scene.layout.toJSON());
      this.sceneLayoutsToUpdate.set(sceneId, layout);
    }
    return layout;
  }

  /**
   * Gets the binding for a slot, logging a warning if not found.
   */
  getBinding(
    layout: ILayoutSegment,
    slotName: string,
    sceneId: string,
    context: string,
  ): TrackTransforms | null {
    const binding = layout.bindings?.[slotName];
    if (!binding) {
      logger.warn(
        `RendererSync: binding '${slotName}' missing for scene '${sceneId}', skipping ${context}`,
      );
      return null;
    }
    return binding;
  }

  /**
   * Resolves scene, layout, and binding for a given sceneId and slotName.
   * Returns null if any part of the resolution fails.
   */
  resolveSceneLayoutBinding(
    sceneId: string,
    slotName: string,
    context: string,
  ): { scene: Scene; layout: ILayoutSegment; binding: TrackTransforms } | null {
    if (!sceneId || !slotName) {
      return null;
    }

    const scene = this.getOrCacheScene(sceneId);
    if (!scene) {
      return null;
    }

    const layout = this.getOrCreateLayout(sceneId, scene);
    const binding = this.getBinding(layout, slotName, sceneId, context);
    if (!binding) {
      return null;
    }

    return { scene, layout, binding };
  }

  /**
   * Gets or creates a slot state for the given scene/slot combination.
   */
  getOrCreateSlotState(
    sceneId: string,
    slotName: string,
    layout: ILayoutSegment,
    binding: TrackTransforms,
  ): SlotSyncState {
    const slotKey = `${sceneId}::${slotName}`;
    let slotState = this.slotStates.get(slotKey);
    if (!slotState) {
      const currentKeyframe = Array.isArray(binding.transformKeyframes)
        ? binding.transformKeyframes[0]
        : undefined;
      slotState = {
        sceneId,
        slotName,
        layout,
        binding,
        currentKeyframe,
        differing: [],
      };
      this.slotStates.set(slotKey, slotState);
    }
    return slotState;
  }

  /**
   * Queues a candidate keyframe to the slot state if it differs from the current keyframe.
   * @returns true if the candidate was queued, false if it was skipped (no change)
   */
  queueCandidateIfChanged(
    slotState: SlotSyncState,
    element: ElementState,
    nextKeyframe: CropTransformKeyframe,
    areKeyframesEqual: (
      element: ElementState,
      current: CropTransformKeyframe,
      next: CropTransformKeyframe,
    ) => boolean,
  ): boolean {
    // Extract current keyframe from binding if not yet set on slotState
    if (
      slotState.currentKeyframe == null &&
      Array.isArray(slotState.binding.transformKeyframes) &&
      slotState.binding.transformKeyframes[0] != null
    ) {
      slotState.currentKeyframe = slotState.binding.transformKeyframes[0];
    }

    if (
      slotState.currentKeyframe &&
      areKeyframesEqual(element, slotState.currentKeyframe, nextKeyframe)
    ) {
      return false; // no actual change
    }

    slotState.differing.push({ element, keyframe: nextKeyframe });
    return true;
  }

  /**
   * Returns all slot states for iteration.
   */
  getSlotStates(): IterableIterator<SlotSyncState> {
    return this.slotStates.values();
  }

  /**
   * Returns the scene layouts map for update application.
   */
  getSceneLayoutsToUpdate(): Map<string, ILayoutSegment> {
    return this.sceneLayoutsToUpdate;
  }
}

const logger = LogManager.getLogger('editor:renderer-sync');

const ROUND_PRECISION = 4;

/**
 * Easing value used by LayoutEngine for discrete keyframe transitions.
 * User-added keyframes (via iframe interaction) will have a different easing
 * (typically null/undefined or a standard easing name), allowing us to
 * distinguish them from our generated keyframes.
 */
const STEPS_EASING = 'steps';

/** Shape of a single Creatomate keyframe entry on an element source property. */
interface SourceKeyframeEntry {
  time: number;
  easing?: string | null;
  value: string | number;
}

export class RendererSyncService {
  private readonly layoutIndexStore: SceneLayoutIndex;
  private readonly getScene: (sceneId: string) => Scene | undefined;
  private readonly updateScene: (
    sceneId: string,
    updates: Partial<IScene>,
  ) => boolean;
  private readonly updateScenes?: (
    updates: Array<{ sceneId: string; updates: Partial<IScene> }>,
  ) => void;
  private readonly updateElement?: (
    elementId: string,
    updates: Record<string, any>,
  ) => boolean;
  private readonly updateElements?: (
    updates: Map<string, Record<string, any>>,
  ) => void;
  private readonly normalizeRendererState?: (
    state: RendererState,
  ) => RendererState;
  private readonly getCurrentTime?: () => number;

  constructor(dependencies: RendererSyncDependencies) {
    this.layoutIndexStore = dependencies.layoutIndexStore;
    this.getScene = dependencies.getScene;
    this.updateScene = dependencies.updateScene;
    this.updateScenes = dependencies.updateScenes;
    this.updateElement = dependencies.updateElement;
    this.updateElements = dependencies.updateElements;
    this.normalizeRendererState = dependencies.normalizeRendererState;
    this.getCurrentTime = dependencies.getCurrentTime;
  }

  applyManualRepresentationUpdate(element: ElementState): boolean {
    const elementId = this.extractElementId(element);
    if (!elementId) {
      return false;
    }

    const appliedElementIds = this.applyManualRepresentationUpdates([element]);
    return appliedElementIds.has(elementId);
  }

  applyManualRepresentationUpdates(
    elements: readonly ElementState[],
  ): ReadonlySet<string> {
    if (!elements || elements.length === 0) {
      return new Set<string>();
    }

    const sceneCache = new Map<string, Scene | null>();
    const pendingUpdates = new Map<
      string,
      { layout: ILayoutSegment; elementIds: Set<string> }
    >();

    for (const element of elements) {
      const elementId = this.extractElementId(element);
      if (!elementId) {
        continue;
      }

      const representationRecord =
        this.layoutIndexStore.getRepresentationRoleRecordFor(
          elementId,
          this.getCurrentTime?.(),
        );
      if (!representationRecord) {
        continue;
      }

      const { sceneId, slotName } = representationRecord.link;
      if (!sceneId || !slotName) {
        continue;
      }

      const scene = this.getCachedScene(sceneCache, sceneId);
      if (!scene) {
        continue;
      }

      let pending = pendingUpdates.get(sceneId);
      if (!pending) {
        pending = {
          layout: deepClone(scene.layout.toJSON()),
          elementIds: new Set<string>(),
        };
        pendingUpdates.set(sceneId, pending);
      }

      const binding = pending.layout.bindings?.[slotName];
      if (!binding) {
        logger.warn(
          `RendererSync: binding '${slotName}' missing for scene '${sceneId}', skipping manual override`,
        );
        continue;
      }

      const updatedBinding = this.buildUpdatedBinding(binding, element);
      if (!updatedBinding) {
        continue;
      }

      pending.layout.bindings[slotName] = updatedBinding;
      pending.elementIds.add(elementId);
    }

    const appliedIds = new Set<string>();

    for (const [sceneId, pending] of pendingUpdates.entries()) {
      if (pending.elementIds.size === 0) {
        continue;
      }

      const success = this.updateScene(sceneId, { layout: pending.layout });
      if (!success) {
        logger.warn(
          `RendererSync: failed to persist layout updates for scene '${sceneId}'`,
        );
        continue;
      }

      for (const elementId of pending.elementIds) {
        appliedIds.add(elementId);
      }
    }

    return appliedIds;
  }

  /**
   * Write the same crop into one named slot of several scenes at once — the
   * reframe modal's "Apply framing to all scenes".
   *
   * `applyManualRepresentationUpdates` resolves the single scene under the
   * playhead from the layout index, which is what keeps an ordinary Apply to
   * the scene being framed (PLAT-11825). This takes the target scenes from the
   * caller instead and matches slots by name: a scene whose layout has no
   * binding under that name is left with its own framing rather than being
   * given a slot it has no place for.
   *
   * @returns the ids of the scenes whose layout actually changed.
   */
  applyManualSlotUpdatesToScenes(
    updates: ReadonlyArray<{ slotName: string; element: ElementState }>,
    sceneIds: readonly string[],
  ): ReadonlySet<string> {
    return this.commitSceneUpdates(
      this.buildManualSlotSceneUpdates(updates, sceneIds),
    );
  }

  /**
   * The scene layouts `applyManualSlotUpdatesToScenes` writes, without
   * writing them — for a caller that commits other scenes' updates in the
   * same command (the reframe modal's unmapped-speaker apply, PLAT-11992).
   */
  buildManualSlotSceneUpdates(
    updates: ReadonlyArray<{ slotName: string; element: ElementState }>,
    sceneIds: readonly string[],
  ): Array<{ sceneId: string; updates: Partial<IScene> }> {
    if (updates.length === 0 || sceneIds.length === 0) {
      return [];
    }

    const sceneCache = new Map<string, Scene | null>();
    const sceneUpdates: Array<{ sceneId: string; updates: Partial<IScene> }> =
      [];

    for (const sceneId of sceneIds) {
      const scene = this.getCachedScene(sceneCache, sceneId);
      if (!scene) {
        continue;
      }

      const layout = deepClone(scene.layout.toJSON());
      let changed = false;

      for (const { slotName, element } of updates) {
        const binding = layout.bindings?.[slotName];
        if (!binding) {
          continue;
        }

        // Null when the scene is already framed this way — nothing to write,
        // and no command to spend an undo entry on.
        const updatedBinding = this.buildUpdatedBinding(binding, element);
        if (!updatedBinding) {
          continue;
        }

        layout.bindings[slotName] = updatedBinding;
        changed = true;
      }

      if (changed) {
        sceneUpdates.push({ sceneId, updates: { layout } });
      }
    }

    return sceneUpdates;
  }

  /**
   * Writes scene updates as one command where the host supports it, so a
   * single undo reverts them all.
   *
   * @returns the ids of the scenes written.
   */
  commitSceneUpdates(
    sceneUpdates: ReadonlyArray<{ sceneId: string; updates: Partial<IScene> }>,
  ): ReadonlySet<string> {
    const updatedSceneIds = new Set<string>();
    if (sceneUpdates.length === 0) {
      return updatedSceneIds;
    }

    // One command for every scene, so a single undo reverts the whole apply.
    if (this.updateScenes) {
      this.updateScenes([...sceneUpdates]);
      for (const { sceneId } of sceneUpdates) {
        updatedSceneIds.add(sceneId);
      }
      return updatedSceneIds;
    }

    for (const { sceneId, updates } of sceneUpdates) {
      if (!this.updateScene(sceneId, updates)) {
        logger.warn(
          `RendererSync: failed to persist layout updates for scene '${sceneId}'`,
        );
        continue;
      }
      updatedSceneIds.add(sceneId);
    }

    return updatedSceneIds;
  }

  sync(prevState: RendererState | undefined, newState: RendererState): void {
    if (!newState?.elements?.length) {
      return;
    }

    const normalizedCurrent = this.normalizeState(newState);
    if (!normalizedCurrent.elements.length) {
      return;
    }

    // Process changes immediately to detect updates
    this.processChanges(prevState, normalizedCurrent);
  }

  private processChanges(
    prevState: RendererState | undefined,
    newState: RendererState,
  ): void {
    // Initialize shared state and helper
    const slotStates = new Map<string, SlotSyncState>();
    const sceneLayoutsToUpdate = new Map<string, ILayoutSegment>();
    const sceneCache = new Map<string, Scene | null>();

    const helper = new SlotStateSyncHelper(
      slotStates,
      sceneLayoutsToUpdate,
      sceneCache,
      this.getScene,
    );

    // Build prev element map and collect changes
    const prevElementsById = this.buildPrevElementsMap(prevState);
    const nonSceneElementUpdates = new Map<string, Record<string, any>>();

    // Process each element
    for (const element of this.iterateElements(newState.elements)) {
      this.processElement(
        element,
        prevElementsById,
        newState,
        helper,
        nonSceneElementUpdates,
      );
    }

    // Resolve conflicts and apply updates
    const scenesNeedingUpdate = this.resolveSlotConflictsAndQueueUpdates(
      helper.getSlotStates(),
    );
    this.applySceneLayoutUpdates(scenesNeedingUpdate, sceneLayoutsToUpdate);
    this.applyNonSceneElementUpdates(nonSceneElementUpdates);
  }

  /**
   * Builds a map of previous element states by ID for efficient change detection.
   */
  private buildPrevElementsMap(
    prevState: RendererState | undefined,
  ): Map<string, ElementState> {
    const prevElementsById = new Map<string, ElementState>();
    if (prevState?.elements) {
      for (const element of this.iterateElements(prevState.elements)) {
        const elementId = this.extractElementId(element);
        if (elementId) {
          prevElementsById.set(elementId, element);
        }
      }
    }
    return prevElementsById;
  }

  /**
   * Processes a single element during sync, routing to user-keyframe or standard path.
   */
  private processElement(
    element: ElementState,
    prevElementsById: Map<string, ElementState>,
    rendererState: RendererState,
    helper: SlotStateSyncHelper,
    nonSceneElementUpdates: Map<string, Record<string, any>>,
  ): void {
    const elementId = this.extractElementId(element);
    if (!elementId) {
      return;
    }

    // Only process elements that have actually changed from prevState
    const prevElement = prevElementsById.get(elementId);
    if (prevElement && !this.hasElementChanged(prevElement, element)) {
      return;
    }

    // Try user-keyframe path first (for merged video elements)
    if (
      this.processUserKeyframeChange(element, elementId, rendererState, helper)
    ) {
      return; // handled
    }

    // Standard path: lookup representation record
    const representationRecord =
      this.layoutIndexStore.getRepresentationRoleRecordFor(
        elementId,
        this.getCurrentTime?.(),
      );

    if (!representationRecord) {
      // Not a scene element - check if it's a non-scene element that needs updating
      const changedProps = this.extractChangedProperties(prevElement, element);
      if (changedProps && Object.keys(changedProps).length > 0) {
        nonSceneElementUpdates.set(elementId, changedProps);
      }
      return;
    }

    this.processSceneElementChange(
      element,
      representationRecord.link.sceneId,
      representationRecord.link.slotName,
      rendererState,
      helper,
    );
  }

  /**
   * Processes a scene element change: resolves binding and queues keyframe update.
   */
  private processSceneElementChange(
    element: ElementState,
    sceneId: string,
    slotName: string,
    rendererState: RendererState,
    helper: SlotStateSyncHelper,
    referenceTime?: number,
  ): boolean {
    const resolved = helper.resolveSceneLayoutBinding(
      sceneId,
      slotName,
      'manual override sync',
    );
    if (!resolved) {
      return false;
    }

    const { layout, binding } = resolved;
    const currentKeyframe = Array.isArray(binding.transformKeyframes)
      ? binding.transformKeyframes[0]
      : undefined;

    const nextKeyframe = this.buildKeyframeFromElement(
      element,
      currentKeyframe,
      rendererState,
      referenceTime,
    );
    if (!nextKeyframe) {
      return false;
    }

    const slotState = helper.getOrCreateSlotState(
      sceneId,
      slotName,
      layout,
      binding,
    );

    return helper.queueCandidateIfChanged(
      slotState,
      element,
      nextKeyframe,
      this.areKeyframesEqual.bind(this),
    );
  }

  /**
   * Resolves conflicts in slot states and queues scenes for update.
   * Returns the set of scene IDs that need updating.
   */
  private resolveSlotConflictsAndQueueUpdates(
    slotStates: IterableIterator<SlotSyncState>,
  ): Set<string> {
    const scenesNeedingUpdate = new Set<string>();

    for (const slotState of slotStates) {
      if (slotState.differing.length === 0) {
        continue;
      }

      const uniqueKeyframes = new Map<string, Candidate>();
      for (const candidate of slotState.differing) {
        const key = this.serializeKeyframe(candidate);
        if (!uniqueKeyframes.has(key)) {
          uniqueKeyframes.set(key, candidate);
        }
      }

      if (uniqueKeyframes.size > 1) {
        logger.warn(
          `RendererSync: conflicting manual layout updates detected for scene '${slotState.sceneId}' slot '${slotState.slotName}', skipping sync`,
        );
        continue;
      }

      const firstEntry = uniqueKeyframes.values().next().value;
      if (!firstEntry) {
        continue;
      }

      const { element: candidateElement } = firstEntry;
      const updatedBinding = this.buildUpdatedBinding(
        slotState.binding,
        candidateElement,
      );
      if (!updatedBinding) {
        continue;
      }

      slotState.layout.bindings[slotState.slotName] = updatedBinding;
      scenesNeedingUpdate.add(slotState.sceneId);
    }

    return scenesNeedingUpdate;
  }

  /**
   * Applies scene layout updates, batching when possible.
   */
  private applySceneLayoutUpdates(
    scenesNeedingUpdate: Set<string>,
    sceneLayoutsToUpdate: Map<string, ILayoutSegment>,
  ): void {
    if (scenesNeedingUpdate.size === 0) {
      return;
    }

    // Batch updates if updateScenes callback is available
    if (scenesNeedingUpdate.size > 1 && this.updateScenes) {
      const updates: Array<{ sceneId: string; updates: Partial<IScene> }> = [];
      for (const sceneId of scenesNeedingUpdate) {
        const layout = sceneLayoutsToUpdate.get(sceneId);
        if (layout) {
          updates.push({ sceneId, updates: { layout } });
        }
      }
      if (updates.length > 0) {
        this.updateScenes(updates);
      }
    } else {
      // Fall back to individual updates
      for (const sceneId of scenesNeedingUpdate) {
        const layout = sceneLayoutsToUpdate.get(sceneId);
        if (!layout) {
          continue;
        }

        const success = this.updateScene(sceneId, { layout });
        if (!success) {
          logger.warn(
            `RendererSync: failed to persist layout updates for scene '${sceneId}'`,
          );
        }
      }
    }
  }

  private normalizeState(state: RendererState): RendererState {
    if (!this.normalizeRendererState) {
      return state;
    }
    return this.normalizeRendererState(state);
  }

  private *iterateElements(
    elements: readonly ElementState[],
  ): Iterable<ElementState> {
    for (const element of elements) {
      yield element;
      if (Array.isArray(element.elements) && element.elements.length > 0) {
        yield* this.iterateElements(element.elements);
      }
    }
  }

  private extractElementId(element: ElementState): string | undefined {
    const candidate = element?.source?.id;
    return typeof candidate === 'string' && candidate.length > 0
      ? candidate
      : undefined;
  }

  private hasElementChanged(
    prevElement: ElementState,
    newElement: ElementState,
  ): boolean {
    const changedProps = this.extractChangedProperties(prevElement, newElement);
    return changedProps !== null && Object.keys(changedProps).length > 0;
  }

  /**
   * Gets or caches a scene, logging a warning if not found.
   * Used by methods that maintain their own local cache.
   */
  private getCachedScene(
    sceneCache: Map<string, Scene | null>,
    sceneId: string,
  ): Scene | null {
    if (sceneCache.has(sceneId)) {
      return sceneCache.get(sceneId) ?? null;
    }

    const scene = this.getScene(sceneId);
    if (!scene) {
      logger.warn(
        `RendererSync: scene '${sceneId}' missing while syncing manual layout changes`,
      );
      sceneCache.set(sceneId, null);
      return null;
    }

    sceneCache.set(sceneId, scene);
    return scene;
  }

  private buildUpdatedBinding(
    binding: TrackTransforms,
    element: ElementState,
  ): TrackTransforms | null {
    const bindingClone = deepClone(binding);
    const currentKeyframe = Array.isArray(bindingClone.transformKeyframes)
      ? bindingClone.transformKeyframes[0]
      : undefined;

    const nextKeyframe = this.buildKeyframeFromElement(
      element,
      currentKeyframe,
    );
    if (!nextKeyframe) {
      return null;
    }

    if (
      currentKeyframe &&
      this.areKeyframesEqual(element, currentKeyframe, nextKeyframe)
    ) {
      return null;
    }

    if (
      Array.isArray(bindingClone.transformKeyframes) &&
      bindingClone.transformKeyframes.length > 0
    ) {
      bindingClone.transformKeyframes[0] = nextKeyframe;
    } else {
      bindingClone.transformKeyframes = [nextKeyframe];
    }

    return bindingClone;
  }

  /**
   * Detects and processes user-added keyframes on merged video elements.
   *
   * Merged elements always have ALL positional properties as keyframe arrays
   * (generated with easing: 'steps').  When the user interacts in the iframe,
   * Creatomate appends a new entry whose easing ≠ 'steps'.
   *
   * This method:
   * 1. Scans all positional props for user-added keyframes
   * 2. Validates that all user keyframes share the same timestamp (rejects otherwise)
   * 3. Converts to absolute timeline time and resolves the target scene
   * 4. Delegates to processSceneElementChange for binding update
   *
   * @returns true if the element was handled (has user keyframes), false otherwise
   */
  private processUserKeyframeChange(
    element: ElementState,
    elementId: string,
    rendererState: RendererState,
    helper: SlotStateSyncHelper,
  ): boolean {
    // Detect user keyframes and validate timestamps
    const detection = this.detectUserKeyframes(element);
    if (!detection) {
      return false; // no user keyframe — fall through to standard path
    }

    const { userKeyframeTime, propsWithUserKeyframes, hasConflictingTimes } =
      detection;

    // Multiple user keyframes at different timestamps in the same sync call
    // are not a valid state — Creatomate should set them all at the playhead.
    // Reject to avoid routing changes to the wrong scene.
    if (hasConflictingTimes) {
      logger.warn(
        `RendererSync: user keyframes at conflicting timestamps on element '${elementId}', ` +
          `props: [${propsWithUserKeyframes.join(', ')}] — rejecting change`,
      );
      return true;
    }

    // Find the correct scene binding for this keyframe time.
    // userKeyframeTime is relative to the element within its composition.
    // Convert to absolute timeline time using the element's global time offset.
    const absoluteKeyframeTime = element.globalTime + userKeyframeTime;

    // Use findRepresentationRecordByTimeRange to find the scene binding
    // whose compositionTime range contains the absolute keyframe time.
    // No fallback — if the scene can't be identified by exact range match, log and bail.
    const targetRecord =
      this.layoutIndexStore.findRepresentationRecordByTimeRange?.(
        elementId,
        absoluteKeyframeTime,
      );

    if (!targetRecord) {
      logger.warn(
        `RendererSync: no scene binding found for user keyframe at ` +
          `t=${absoluteKeyframeTime.toFixed(3)}s (element-relative=${userKeyframeTime.toFixed(3)}s) ` +
          `on element '${elementId}', changed props: [${propsWithUserKeyframes.join(', ')}]`,
      );
      return true; // handled — don't fall through to standard path
    }

    const { sceneId, slotName } = targetRecord.link;
    if (!sceneId || !slotName) {
      return true;
    }

    // Delegate to shared method with referenceTime for correct keyframe resolution
    const queued = this.processSceneElementChange(
      element,
      sceneId,
      slotName,
      rendererState,
      helper,
      userKeyframeTime,
    );

    if (queued) {
      logger.debug(
        `RendererSync: user keyframe detected at t=${userKeyframeTime.toFixed(3)}s ` +
          `(abs=${absoluteKeyframeTime.toFixed(3)}s) on element '${elementId}', ` +
          `routed to scene '${sceneId}' slot '${slotName}', ` +
          `changed props: [${propsWithUserKeyframes.join(', ')}]`,
      );
    }

    return true;
  }

  /**
   * Detects user-added keyframes on positional properties.
   * Returns null if no user keyframes found, otherwise returns detection result.
   */
  private detectUserKeyframes(element: ElementState): {
    userKeyframeTime: number;
    propsWithUserKeyframes: string[];
    hasConflictingTimes: boolean;
  } | null {
    const propsWithUserKeyframes: string[] = [];
    let userKeyframeTime: number | undefined;
    let hasConflictingTimes = false;

    for (const prop of POSITIONAL_PROPS) {
      const time = this.getUserKeyframeTime(element.source?.[prop]);
      if (time !== undefined) {
        propsWithUserKeyframes.push(prop);
        if (userKeyframeTime === undefined) {
          userKeyframeTime = time;
        } else if (Math.abs(time - userKeyframeTime) > POSITION_EPSILON) {
          hasConflictingTimes = true;
        }
      }
    }

    if (userKeyframeTime === undefined) {
      return null;
    }

    return { userKeyframeTime, propsWithUserKeyframes, hasConflictingTimes };
  }

  private buildKeyframeFromElement(
    element: ElementState,
    existingKeyframe?: CropTransformKeyframe,
    rendererState?: RendererState,
    referenceTime?: number,
  ): CropTransformKeyframe | null {
    const rect = this.extractCreatomateRect(element, referenceTime);
    if (!rect) {
      return null;
    }

    const normalizedCrop = creatomateToNormalizedCrop(
      rect.x,
      rect.y,
      rect.width,
      rect.height,
    );

    if (!this.isFiniteCrop(normalizedCrop)) {
      return null;
    }

    const crop = {
      x: this.round(normalizedCrop.x),
      y: this.round(normalizedCrop.y),
      w: this.round(Math.max(normalizedCrop.w, 0)),
      h: this.round(Math.max(normalizedCrop.h, 0)),
    };

    const scalePercent = parsePercent(
      this.resolveSourceProperty(element.source?.x_scale, referenceTime),
    );
    const anchorXPercent = parsePercent(
      this.resolveSourceProperty(element.source?.x_anchor, referenceTime),
    );
    const anchorYPercent = parsePercent(
      this.resolveSourceProperty(element.source?.y_anchor, referenceTime),
    );

    const nextScale = this.round(
      scalePercent !== undefined ? scalePercent / 100 : 1,
    );

    const defaultAnchor = { x: 0.5, y: 0.5 };
    const anchor = {
      x: this.round(
        anchorXPercent !== undefined ? anchorXPercent / 100 : defaultAnchor.x,
      ),
      y: this.round(
        anchorYPercent !== undefined ? anchorYPercent / 100 : defaultAnchor.y,
      ),
    };

    const cropAspectRatio = this.resolveCropAspectRatio(
      element,
      existingKeyframe,
      rendererState,
    );

    const textPosition =
      element.source?.type === 'text'
        ? existingKeyframe?.textPosition
          ? this.extractTextPosition(element) // scene has override → update it
          : null // no override → don't create one, inherit global config
        : null;

    return {
      timelineAnchor: existingKeyframe?.timelineAnchor ?? 'clip-start',
      crop,
      scale: nextScale,
      anchor,
      ease: existingKeyframe?.ease ?? 'linear',
      cropAspectRatio,
      textPosition: textPosition ?? existingKeyframe?.textPosition,
    };
  }

  // ── Keyframe array helpers ──────────────────────────────────────────────────

  /**
   * Checks whether a source property value is a Creatomate keyframe array
   * (array of {time, easing, value} objects) as opposed to a scalar string/number.
   */
  private isKeyframeArray(value: unknown): value is SourceKeyframeEntry[] {
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      value[0] !== null &&
      'time' in value[0] &&
      'value' in value[0]
    );
  }

  /**
   * Finds user-added keyframe entries in an array.
   * LayoutEngine always stamps generated keyframes with `easing: 'steps'`.
   * When the user interacts with the element in the iframe, Creatomate adds
   * a new entry whose easing is null/undefined or a standard easing name.
   */
  private findUserAddedKeyframes(
    keyframes: SourceKeyframeEntry[],
  ): SourceKeyframeEntry[] {
    return keyframes.filter(kf => kf.easing !== STEPS_EASING);
  }

  /**
   * Resolves a source property that may be either a scalar or a keyframe array
   * to a scalar value suitable for parsePercent / rect extraction.
   *
   * When the value is a keyframe array:
   * - If a user-added keyframe exists (easing ≠ 'steps'), returns its value
   * - Otherwise resolves to the value at referenceTime by finding the last
   *   keyframe whose time ≤ referenceTime (step interpolation)
   *
   * @param value - The raw source property value
   * @param referenceTime - Optional time for step-resolving keyframe arrays
   * @returns Scalar string/number suitable for parsePercent, or undefined
   */
  private resolveSourceProperty(
    value: unknown,
    referenceTime?: number,
  ): string | number | undefined {
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    if (!this.isKeyframeArray(value)) {
      return undefined;
    }

    // Prefer user-added keyframe (the one the user just dragged to)
    const userKeyframes = this.findUserAddedKeyframes(value);
    if (userKeyframes.length > 0) {
      // If multiple user keyframes exist, pick the latest one
      const latest = userKeyframes[userKeyframes.length - 1];
      return latest.value;
    }

    // Fall back: step-resolve at referenceTime
    if (referenceTime !== undefined) {
      return this.resolveKeyframeArrayAtTime(value, referenceTime);
    }

    // Last resort: return the first entry's value
    return value[0].value;
  }

  /**
   * Step-interpolation: returns the value of the last keyframe whose time ≤ referenceTime.
   * Keyframes are expected to use 'steps' easing (discrete, no interpolation).
   */
  private resolveKeyframeArrayAtTime(
    keyframes: SourceKeyframeEntry[],
    referenceTime: number,
  ): string | number {
    // Keyframes should be sorted by time but sort defensively
    const sorted = [...keyframes].sort((a, b) => a.time - b.time);

    let resolved = sorted[0].value;
    for (const kf of sorted) {
      if (kf.time <= referenceTime + POSITION_EPSILON) {
        resolved = kf.value;
      } else {
        break;
      }
    }
    return resolved;
  }

  /**
   * Given a keyframe array from the renderer's element source, finds the composition-relative
   * time of any user-added keyframe. Returns undefined if no user keyframe is present.
   *
   * This time is relative to the element start within its composition, matching the
   * coordinate system used by mergeContiguousVideoChildren.
   */
  private getUserKeyframeTime(value: unknown): number | undefined {
    if (!this.isKeyframeArray(value)) {
      return undefined;
    }
    const userKeyframes = this.findUserAddedKeyframes(value);
    if (userKeyframes.length === 0) {
      return undefined;
    }
    return userKeyframes[userKeyframes.length - 1].time;
  }

  /**
   * Returns true if the source property value is a keyframe array containing
   * at least one user-added keyframe (easing ≠ 'steps').
   */
  private hasUserAddedKeyframe(value: unknown): boolean {
    if (!this.isKeyframeArray(value)) {
      return false;
    }
    return this.findUserAddedKeyframes(value).length > 0;
  }

  private extractCreatomateRect(
    element: ElementState,
    referenceTime?: number,
  ): { x: number; y: number; width: number; height: number } | null {
    const defaultRect = {
      x: 50,
      y: 50,
      width: 100,
      height: 100,
    };

    const resolvedX =
      parsePercent(
        this.resolveSourceProperty(element.source?.x, referenceTime),
      ) ?? defaultRect.x;
    const resolvedY =
      parsePercent(
        this.resolveSourceProperty(element.source?.y, referenceTime),
      ) ?? defaultRect.y;

    const parsedWidth = parsePercent(
      this.resolveSourceProperty(element.source?.width, referenceTime),
    );
    const parsedHeight = parsePercent(
      this.resolveSourceProperty(element.source?.height, referenceTime),
    );

    const resolvedWidth =
      parsedWidth !== undefined && parsedWidth > 0
        ? parsedWidth
        : defaultRect.width;
    const resolvedHeight =
      parsedHeight !== undefined && parsedHeight > 0
        ? parsedHeight
        : defaultRect.height;

    if (resolvedWidth <= 0 || resolvedHeight <= 0) {
      return null;
    }

    return {
      x: resolvedX,
      y: resolvedY,
      width: resolvedWidth,
      height: resolvedHeight,
    };
  }

  private resolveCropAspectRatio(
    element: ElementState,
    existingKeyframe: CropTransformKeyframe | undefined,
    rendererState?: RendererState,
  ): number | undefined {
    if (element.source?.type === 'text') {
      return existingKeyframe?.cropAspectRatio;
    }

    const explicit = this.extractExplicitCropAspectRatio(element);
    if (explicit !== undefined) {
      return this.round(explicit);
    }

    return this.deriveCropAspectRatioFromElementDimensions(
      element,
      rendererState,
      existingKeyframe,
    );
  }

  private extractExplicitCropAspectRatio(
    element: ElementState,
  ): number | undefined {
    const value = element.source?.cropAspectRatio;
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
    return undefined;
  }

  /**
   * Derives cropAspectRatio from element display dimensions relative to current output.
   *
   * When a user adjusts element dimensions in the iframe, they're working in the context
   * of the current output aspect ratio (which may differ from the original video AR).
   *
   * Formula: cropAspectRatio = currentOutputAR × (elementWidth% / elementHeight%)
   *
   * Example: 16:9 output, element at 80%×100% → 1.778 × (80/100) = 1.422
   *
   * Note: ReframeVideoModal uses source video AR because it displays a canvas at that AR,
   * but in the iframe, the user works in the current output AR (could be 9:16, 1:1, etc.)
   *
   * @param element - Element with width/height percentages
   * @param rendererState - State containing current output width/height
   * @param existingKeyframe - Fallback if calculation not possible
   * @returns Final display aspect ratio, or existing value if cannot be calculated
   */
  private deriveCropAspectRatioFromElementDimensions(
    element: ElementState,
    rendererState: RendererState | undefined,
    existingKeyframe?: CropTransformKeyframe,
  ): number | undefined {
    // Get current output aspect ratio (the canvas the user is working in)
    const isValidRendererSize =
      rendererState &&
      Number.isFinite(rendererState.width) &&
      Number.isFinite(rendererState.height) &&
      rendererState.width > 0 &&
      rendererState.height > 0;

    if (!isValidRendererSize) {
      // Fallback to existing value if no output dimensions
      return existingKeyframe?.cropAspectRatio;
    }

    const outputAspectRatio = rendererState.width / rendererState.height;

    // Parse element dimensions as percentages
    const width =
      parsePercent(this.resolveSourceProperty(element.source?.width)) ?? 100;
    const height =
      parsePercent(this.resolveSourceProperty(element.source?.height)) ?? 100;

    if (height <= 0) {
      return existingKeyframe?.cropAspectRatio;
    }

    // Calculate final display aspect ratio based on current output
    const cropAspectRatio = outputAspectRatio * (width / height);
    return this.round(cropAspectRatio);
  }

  private isFiniteCrop(crop: {
    x: number;
    y: number;
    w: number;
    h: number;
  }): boolean {
    return [crop.x, crop.y, crop.w, crop.h].every(value =>
      Number.isFinite(value),
    );
  }

  private extractTextPosition(
    element: ElementState,
  ): { x?: string; y?: string; x_alignment?: string } | null {
    // Check if element has text positioning properties (karaoke/text elements)
    const x = element.source?.x;
    const y = element.source?.y;
    const x_alignment = element.source?.x_alignment;

    // Only extract if at least one property is present
    if (x == null && y == null && x_alignment == null) {
      return null;
    }

    return {
      x: x != null ? String(x) : undefined,
      y: y != null ? String(y) : undefined,
      x_alignment: x_alignment != null ? String(x_alignment) : undefined,
    };
  }

  private serializeKeyframe(candidate: Candidate): string {
    return candidate.element.source?.type === 'text'
      ? this.serializeKeyframeTextPosition(candidate.keyframe)
      : this.serializeKeyframeCrop(candidate.keyframe);
  }

  private serializeKeyframeCrop(keyframe: CropTransformKeyframe): string {
    return [
      this.round(keyframe.crop.x),
      this.round(keyframe.crop.y),
      this.round(keyframe.crop.w),
      this.round(keyframe.crop.h),
      this.round(keyframe.scale),
      this.round(keyframe.anchor.x),
      this.round(keyframe.anchor.y),
    ].join('|');
  }

  private serializeKeyframeTextPosition(
    keyframe: CropTransformKeyframe,
  ): string {
    if (!keyframe.textPosition) {
      return '';
    }
    return [
      keyframe.textPosition.x ?? '',
      keyframe.textPosition.y ?? '',
      keyframe.textPosition.x_alignment ?? '',
    ].join('|');
  }

  private keyframesCropApproximatelyEqual(
    current: CropTransformKeyframe,
    next: CropTransformKeyframe,
  ): boolean {
    return (
      isApproxEqual(current.crop.x, next.crop.x) &&
      isApproxEqual(current.crop.y, next.crop.y) &&
      isApproxEqual(current.crop.w, next.crop.w) &&
      isApproxEqual(current.crop.h, next.crop.h) &&
      isApproxEqual(current.scale, next.scale) &&
      isApproxEqual(current.anchor.x, next.anchor.x) &&
      isApproxEqual(current.anchor.y, next.anchor.y)
    );
  }

  private keyframesTextPositionApproximatelyEqual(
    current: CropTransformKeyframe,
    next: CropTransformKeyframe,
  ): boolean {
    if (!current.textPosition && !next.textPosition) {
      return true;
    }
    if (!current.textPosition || !next.textPosition) {
      return false;
    }

    // Parse percentage strings as numbers and compare with tolerance
    // to avoid false positives from precision differences (e.g., "4.177734375%" vs "4.1777%")
    const currentX = parsePercent(current.textPosition.x);
    const nextX = parsePercent(next.textPosition.x);
    const currentY = parsePercent(current.textPosition.y);
    const nextY = parsePercent(next.textPosition.y);
    const currentXAlignment = parsePercent(current.textPosition.x_alignment);
    const nextXAlignment = parsePercent(next.textPosition.x_alignment);

    return (
      isApproxEqual(currentX ?? 50, nextX ?? 50) &&
      isApproxEqual(currentY ?? 50, nextY ?? 50) &&
      isApproxEqual(currentXAlignment ?? 50, nextXAlignment ?? 50)
    );
  }

  private areKeyframesEqual(
    element: ElementState,
    currentKeyframe: CropTransformKeyframe,
    nextKeyframe: CropTransformKeyframe,
  ): boolean {
    const elementType = element.source?.type;
    if (elementType === 'text') {
      return this.keyframesTextPositionApproximatelyEqual(
        currentKeyframe,
        nextKeyframe,
      );
    } else {
      return this.keyframesCropApproximatelyEqual(
        currentKeyframe,
        nextKeyframe,
      );
    }
  }

  private round(value: number): number {
    const factor = Math.pow(10, ROUND_PRECISION);
    return Math.round(value * factor) / factor;
  }

  /**
   * Extracts changed properties for non-scene elements.
   * Compares positional properties between previous and current element state.
   *
   * When a property is a keyframe array containing a user-added keyframe
   * (easing ≠ 'steps'), that property is considered changed regardless of
   * whether the previous state had the same resolved value.
   */
  private extractChangedProperties(
    prevElement: ElementState | undefined,
    newElement: ElementState,
  ): Record<string, any> | null {
    if (!prevElement) {
      return null;
    }

    const changes: Record<string, any> = {};
    const elementType = newElement.source?.type;

    if (elementType === 'text') {
      // For text elements, check text positioning properties
      const prevX = parsePercent(
        this.resolveSourceProperty(prevElement.source?.x),
      );
      const prevY = parsePercent(
        this.resolveSourceProperty(prevElement.source?.y),
      );
      const prevXAlignment = parsePercent(
        this.resolveSourceProperty(prevElement.source?.x_alignment),
      );
      const prevWidth = parsePercent(
        this.resolveSourceProperty(prevElement.source?.width),
      );
      const prevHeight = parsePercent(
        this.resolveSourceProperty(prevElement.source?.height),
      );

      const newX = parsePercent(
        this.resolveSourceProperty(newElement.source?.x),
      );
      const newY = parsePercent(
        this.resolveSourceProperty(newElement.source?.y),
      );
      const newXAlignment = parsePercent(
        this.resolveSourceProperty(newElement.source?.x_alignment),
      );
      const newWidth = parsePercent(
        this.resolveSourceProperty(newElement.source?.width),
      );
      const newHeight = parsePercent(
        this.resolveSourceProperty(newElement.source?.height),
      );

      if (!isApproxEqual(prevX ?? 50, newX ?? 50)) {
        changes.x = this.resolveSourceProperty(newElement.source.x);
      }
      if (!isApproxEqual(prevY ?? 50, newY ?? 50)) {
        changes.y = this.resolveSourceProperty(newElement.source.y);
      }
      if (!isApproxEqual(prevXAlignment ?? 50, newXAlignment ?? 50)) {
        changes.x_alignment = this.resolveSourceProperty(
          newElement.source.x_alignment,
        );
      }
      if (!isApproxEqual(prevWidth ?? 100, newWidth ?? 100)) {
        changes.width = this.resolveSourceProperty(newElement.source.width);
      }
      if (!isApproxEqual(prevHeight ?? 100, newHeight ?? 100)) {
        changes.height = this.resolveSourceProperty(newElement.source.height);
      }
    } else {
      // For non-text elements, check standard positional properties
      // Also detect user-added keyframes as an explicit signal of change

      for (const prop of POSITIONAL_PROPS) {
        if (this.hasUserAddedKeyframe(newElement.source?.[prop])) {
          changes[prop] = this.resolveSourceProperty(newElement.source[prop]);
          continue;
        }
      }

      const prevRect = this.extractCreatomateRect(prevElement);
      const prevScale = parsePercent(
        this.resolveSourceProperty(prevElement.source?.x_scale),
      );
      const prevAnchorX = parsePercent(
        this.resolveSourceProperty(prevElement.source?.x_anchor),
      );
      const prevAnchorY = parsePercent(
        this.resolveSourceProperty(prevElement.source?.y_anchor),
      );

      const newRect = this.extractCreatomateRect(newElement);
      const newScale = parsePercent(
        this.resolveSourceProperty(newElement.source?.x_scale),
      );
      const newAnchorX = parsePercent(
        this.resolveSourceProperty(newElement.source?.x_anchor),
      );
      const newAnchorY = parsePercent(
        this.resolveSourceProperty(newElement.source?.y_anchor),
      );

      if (prevRect && newRect) {
        if (!changes.x && !isApproxEqual(prevRect.x, newRect.x)) {
          changes.x = this.resolveSourceProperty(newElement.source.x);
        }
        if (!changes.y && !isApproxEqual(prevRect.y, newRect.y)) {
          changes.y = this.resolveSourceProperty(newElement.source.y);
        }
        if (!changes.width && !isApproxEqual(prevRect.width, newRect.width)) {
          changes.width = this.resolveSourceProperty(newElement.source.width);
        }
        if (
          !changes.height &&
          !isApproxEqual(prevRect.height, newRect.height)
        ) {
          changes.height = this.resolveSourceProperty(newElement.source.height);
        }
      }

      if (
        !changes.x_scale &&
        !isApproxEqual(prevScale ?? 100, newScale ?? 100)
      ) {
        changes.x_scale = this.resolveSourceProperty(newElement.source.x_scale);
      }
      if (
        !changes.x_anchor &&
        !isApproxEqual(prevAnchorX ?? 50, newAnchorX ?? 50)
      ) {
        changes.x_anchor = this.resolveSourceProperty(
          newElement.source.x_anchor,
        );
      }
      if (
        !changes.y_anchor &&
        !isApproxEqual(prevAnchorY ?? 50, newAnchorY ?? 50)
      ) {
        changes.y_anchor = this.resolveSourceProperty(
          newElement.source.y_anchor,
        );
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Applies updates to non-scene elements using the updateElement(s) callbacks
   */
  private applyNonSceneElementUpdates(
    updates: Map<string, Record<string, any>>,
  ): void {
    if (updates.size === 0) {
      return;
    }

    if (!this.updateElement && !this.updateElements) {
      logger.warn(
        'RendererSync: no updateElement or updateElements callback provided, skipping non-scene element updates',
      );
      return;
    }

    // Batch updates if updateElements callback is available
    if (updates.size > 1 && this.updateElements) {
      logger.info(
        `RendererSync: applying external updates to ${updates.size} non-scene elements`,
      );
      try {
        this.updateElements(updates);
      } catch (e) {
        logger.error(
          'RendererSync: error applying bulk non-scene element updates',
          e,
        );
      }
    } else if (this.updateElement) {
      // Fall back to individual updates
      for (const [elementId, elementUpdates] of updates.entries()) {
        const success = this.updateElement(elementId, elementUpdates);
        if (!success) {
          logger.warn(
            `RendererSync: failed to persist external updates for non-scene element '${elementId}'`,
          );
        }
      }
    }
  }
}

/**
 * Translate the reset signal from `extractChangedProperties` into concrete
 * model defaults.
 *
 * The differ reports a positional prop the renderer *omitted* (= returned to
 * its default) as present-with-`undefined`. Left as-is, `Element.update` skips
 * `undefined` props ("keep old value") and the reset is silently dropped
 * (PLAT-10700). Here — at the only boundary where present-`undefined` is
 * expected — we replace each such value with the element's own type-correct
 * default (`Element.defaultFor`, overridden by TextElement/ShapeElement).
 * Present-with-a-value props pass through unchanged; absent props were never in
 * `updates`, so they naturally keep their old value.
 */
export function resolveResetProps(
  element: Element | undefined,
  updates: Record<string, any>,
): Record<string, any> {
  if (!element) return updates;
  const resolved: Record<string, any> = {};
  for (const [key, value] of Object.entries(updates)) {
    resolved[key] = value === undefined ? element.defaultFor(key) : value;
  }
  return resolved;
}

/** Map variant of {@link resolveResetProps} for the bulk update path. */
export function resolveResetPropsMap(
  updates: Map<string, Record<string, any>>,
  getElement: (id: string) => Element | undefined,
): Map<string, Record<string, any>> {
  const resolved = new Map<string, Record<string, any>>();
  for (const [id, props] of updates) {
    resolved.set(id, resolveResetProps(getElement(id), props));
  }
  return resolved;
}

export default RendererSyncService;
